import type { ContractorType, PayoutDestination } from 'src/constants/payment/enums'
import type { PaymentConfig, VendorFee } from 'src/interfaces/payment'

/**
 * The four rate columns are `@Column('decimal', { precision: 4, scale: 2 })`, and node-postgres
 * hands decimals back as strings to avoid silent float truncation — so `buyerRate` arrives as
 * `"1.00"`, not `1`. Everything that reads a number off this wire goes through here.
 *
 * The fallback is `0` because the entity's own column default is `0`: reading an absent fee as
 * zero reports what the database would, rather than inventing a figure.
 */
export function toNumber(value: unknown, fallback = 0): number {
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      return fallback
    }

    return value
  }

  if (typeof value !== 'string') {
    return fallback
  }

  const trimmed = value.trim()

  if (trimmed === '') {
    return fallback
  }

  const parsed = Number(trimmed)

  if (!Number.isFinite(parsed)) {
    return fallback
  }

  return parsed
}

function toStringArray<T extends string>(value: unknown): T[] {
  if (!Array.isArray(value)) {
    return []
  }

  return value.filter((entry): entry is T => {
    return typeof entry === 'string'
  })
}

/**
 * `applicableDestinations` and `contractorType` are normalised from absent/null to `[]` rather
 * than left undefined: the DTO's `@IsEnum(…, { each: true })` rejects `undefined` but accepts an
 * empty array, and live rows genuinely carry empty ones.
 *
 * `type` is copied verbatim, including a value this app does not offer. The backend enum has a
 * sixth member (`custom`); rewriting one to `per_payment` while the admin edits an unrelated row
 * would change live pricing that nobody asked to change.
 */
export function parseVendorFee(value: unknown): VendorFee {
  const row = (value ?? {}) as Record<string, unknown>

  return {
    title: typeof row.title === 'string' ? row.title : '',
    type: typeof row.type === 'string' ? row.type : '',
    rate: toNumber(row.rate),
    fee: toNumber(row.fee),
    feeCapCents: toNumber(row.feeCapCents),
    applicableDestinations: toStringArray<PayoutDestination>(row.applicableDestinations),
    contractorType: toStringArray<ContractorType>(row.contractorType),
  }
}

function toPaymentConfig(source: Record<string, unknown>): PaymentConfig {
  const breakdowns = Array.isArray(source.invoiceBreakdowns) ? source.invoiceBreakdowns : []

  return {
    id: typeof source.id === 'string' ? source.id : '',
    payoutType: typeof source.payoutType === 'string' ? source.payoutType : '',
    payoutTimeInHours: toNumber(source.payoutTimeInHours),
    buyerFee: toNumber(source.buyerFee),
    buyerRate: toNumber(source.buyerRate),
    loanTransferFee: toNumber(source.loanTransferFee),
    loanTransferRate: toNumber(source.loanTransferRate),
    wireTransferFee: toNumber(source.wireTransferFee),
    wireTransferRate: toNumber(source.wireTransferRate),
    invoiceBreakdowns: breakdowns.map((row) => {
      return parseVendorFee(row)
    }),
  }
}

/**
 * `GET …/buyer` returns `partner.partnerStitch`, which is `undefined` for a customer with no
 * config row — so Nest sends a 200 with an EMPTY BODY, and `response.json()` would throw a
 * SyntaxError on it. The fetch saga reads the body as text (`returnResponse: true`) and parses
 * it here, so the legacy-customer case cannot surface as a parse crash.
 *
 * An array or malformed JSON THROWS rather than returning `null` — deliberately unlike
 * `parseGigsafeConfigResponse`, which returns `null` for an array. Here `null` means "this
 * customer has no payment configuration", which the page renders as a factual statement about
 * their pricing. A malformed response must not be able to make that claim; it is an anomaly, and
 * the saga's `captureSentryError` should record it.
 */
export function parsePaymentConfigResponse(raw: unknown): PaymentConfig | null {
  if (raw === null || raw === undefined) {
    return null
  }

  if (typeof raw === 'object') {
    if (Array.isArray(raw)) {
      throw new Error('Malformed payment config response')
    }

    return toPaymentConfig(raw as Record<string, unknown>)
  }

  if (typeof raw !== 'string') {
    throw new Error('Malformed payment config response')
  }

  const trimmed = raw.trim()

  if (trimmed === '' || trimmed === 'null') {
    return null
  }

  let parsed: unknown

  try {
    parsed = JSON.parse(trimmed)
  } catch {
    throw new Error('Malformed payment config response')
  }

  if (parsed === null) {
    return null
  }

  if (typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error('Malformed payment config response')
  }

  return toPaymentConfig(parsed as Record<string, unknown>)
}
